// Virtual background pipeline for Visage. Camera frames run through MediaPipe
// selfie segmentation (assets self-hosted under /mediapipe) and composite onto
// a blur or generated backdrop; the canvas captureStream track is swapped onto
// the publisher with RTCRtpSender.replaceTrack.
import { SelfieSegmentation } from '@mediapipe/selfie_segmentation';

const PRESETS = {
  navy: ['#0b1f3a', '#12315d'],
  slate: ['#1f2937', '#374151'],
  warm: ['#3b2f2f', '#6b4f3a'],
};

function presetCanvas(mode, w, h) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  const [a, b] = PRESETS[mode] || PRESETS.navy;
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, a); g.addColorStop(1, b);
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  const v = ctx.createRadialGradient(w / 2, h / 2, h / 3, w / 2, h / 2, h);
  v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,0.45)');
  ctx.fillStyle = v; ctx.fillRect(0, 0, w, h);
  return c;
}

export async function createBackgroundProcessor(rawStream) {
  const videoTrack = rawStream.getVideoTracks()[0];
  const settings = videoTrack.getSettings();
  const w = settings.width || 640;
  const h = settings.height || 480;

  const video = document.createElement('video');
  video.srcObject = new MediaStream([videoTrack]);
  video.muted = true;
  video.playsInline = true;
  await video.play();

  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');

  let mode = 'blur';
  let bg = null;
  let running = true;
  let busy = false;

  const seg = new SelfieSegmentation({ locateFile: (f) => `/mediapipe/${f}` });
  seg.setOptions({ modelSelection: 1 });
  seg.onResults((results) => {
    if (!running) return;
    ctx.save();
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(results.segmentationMask, 0, 0, w, h);
    ctx.globalCompositeOperation = 'source-in';
    ctx.drawImage(results.image, 0, 0, w, h);
    ctx.globalCompositeOperation = 'destination-over';
    if (mode === 'blur') {
      ctx.filter = 'blur(14px)';
      ctx.drawImage(results.image, 0, 0, w, h);
      ctx.filter = 'none';
    } else {
      if (!bg) bg = presetCanvas(mode, w, h);
      ctx.drawImage(bg, 0, 0, w, h);
    }
    ctx.restore();
  });

  const tick = async () => {
    if (!running) return;
    if (!busy && video.readyState >= 2) {
      busy = true;
      try { await seg.send({ image: video }); } catch (e) { /* dropped frame */ }
      busy = false;
    }
    setTimeout(() => { if (running) requestAnimationFrame(tick); }, 1000 / 24);
  };
  requestAnimationFrame(tick);

  const stream = canvas.captureStream(24);
  return {
    stream,
    track: stream.getVideoTracks()[0],
    setMode(m) { mode = m; bg = null; },
    stop() {
      running = false;
      try { seg.close(); } catch (e) {}
      try { video.pause(); } catch (e) {}
      stream.getTracks().forEach(t => t.stop());
    },
  };
}
